import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';

import Navbar from '../../components/navbar/navbar';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';
import { useUser } from '../../context/UserContext';
import { formatAddress } from '../../utils/addressUtils';
import { ListingGridSkeleton } from '../../components/loading-states';
import SEO from '../../components/SEO';
import { generateCollectionPageSchema, generateItemListSchema } from '../../utils/schema';

export default function Agencies() {
  const { user } = useUser();
  const [agencias, setAgencias] = useState<any[]>([]);
  const [filteredAgencias, setFilteredAgencias] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const agenciasPerPage = 12; // Número de agencias por página

  useEffect(() => {
    const fetchAgencias = async () => {
      try {
        const agenciasRef = query(collection(db, 'inmobiliaria'), where('tiene_miembros_oficiales', '==', true));
        const snapshot = await getDocs(agenciasRef);
        
        const agenciasData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setAgencias(agenciasData);
      } catch (error) {
        console.error('Error al traer agencias:', error);
        setError('Error al cargar las agencias. Por favor, intenta de nuevo.');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar las agencias. Por favor, intenta de nuevo más tarde.',
          confirmButtonColor: '#3085d6'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAgencias();
  }, []);

  // Efecto para filtrar agencias por término de búsqueda
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAgencias(agencias);
    } else {
      const filtered = agencias.filter(agencia => 
        agencia.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAgencias(filtered);
    }
    setCurrentPage(1); // Reset a la primera página al filtrar
  }, [agencias, searchTerm]);

  // Calcular agencias para la página actual
  const indexOfLastAgencia = currentPage * agenciasPerPage;
  const indexOfFirstAgencia = indexOfLastAgencia - agenciasPerPage;
  const currentAgencias = filteredAgencias.slice(indexOfFirstAgencia, indexOfLastAgencia);
  const totalPages = Math.ceil(filteredAgencias.length / agenciasPerPage);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisiblePages = Math.min(5, totalPages);
    
    for (let i = 0; i < maxVisiblePages; i++) {
      let pageNumber: number;
      if (totalPages <= 5) {
        pageNumber = i + 1;
      } else if (currentPage <= 3) {
        pageNumber = i + 1;
      } else if (currentPage >= totalPages - 2) {
        pageNumber = totalPages - 4 + i;
      } else {
        pageNumber = currentPage - 2 + i;
      }
      pages.push(pageNumber);
    }
    return pages;
  };

  const getAgencyIcon = (nombre: string) => {
    // Generar icono basado en la primera letra del nombre
    const firstLetter = nombre.charAt(0).toUpperCase();
    const colors = [
      'bg-primary', 'bg-success', 'bg-info', 'bg-warning', 'bg-danger', 
      'bg-secondary', 'bg-dark', 'bg-purple', 'bg-cyan', 'bg-pink'
    ];
    const colorIndex = nombre.length % colors.length;
    return {
      letter: firstLetter,
      colorClass: colors[colorIndex]
    };
  };

  const agenciasSchema = filteredAgencias.length > 0 ? generateItemListSchema(
    filteredAgencias.slice(0, 10).map(a => ({
      id: a.id,
      name: a.nombre,
      url: `https://ampirivieranayarit.com/modulob/agency-page/${a.id}`,
      image: a.logo ? [a.logo] : undefined
    })),
    'Agencias Inmobiliarias AMPI Riviera Nayarit',
    'Directorio de agencias inmobiliarias certificadas en Bahía de Banderas, Nuevo Vallarta y Riviera Nayarit'
  ) : undefined;

  return (
    <>
      <SEO
        title="Agencias Inmobiliarias en Riviera Nayarit"
        description={`Directorio de ${filteredAgencias.length || ''} agencias inmobiliarias certificadas AMPI en Riviera Nayarit. Encuentra inmobiliarias en Bahía de Banderas, Nuevo Vallarta, Sayulita, Punta de Mita y más.`}
        path="/agencies"
        keywords="agencias inmobiliarias riviera nayarit, inmobiliarias bahía de banderas, inmobiliarias nuevo vallarta, agencias certificadas AMPI, bienes raíces nayarit, real estate riviera nayarit"
        schema={agenciasSchema}
      />
      {user ? (
        <UserNav
          nombreUsuario={user.nombre}
          nombreInmobiliaria={user.inmobiliaria_nombre}
        />
      ) : (
        <Navbar transparent={false} />
      )}

      <Breadcrumb
        items={[
          { label: 'Inicio', path: '/' },
          { label: 'Agencias Inmobiliarias', path: '/agencies' }
        ]}
      />

      <section className="bg-primary position-relative">
        <div className="container py-5">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <h1 className="ipt-title text-light mb-3">Agencias Inmobiliarias en Riviera Nayarit</h1>
              <p className="ipn-subtitle text-light-50 mb-0">Directorio oficial de agencias inmobiliarias certificadas AMPI en Bahía de Banderas, Nuevo Vallarta y la Riviera Nayarit.</p>
            </div>
          </div>
          
          {/* Barra de búsqueda */}
          <div className="row justify-content-center mt-4 pb-4">
            <div className="col-lg-6">
              <div className="input-group">
                <input 
                  type="text" 
                  className="form-control form-control-lg" 
                  placeholder="Buscar agencia por nombre..." 
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <span className="input-group-text">
                  <i className="fa-solid fa-search"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="gray-simple">
        <div className="container">
          {/* Información de resultados */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  Mostrando {currentAgencias.length} de {filteredAgencias.length} agencias
                  {searchTerm && ` para "${searchTerm}"`}
                </p>
                {totalPages > 1 && (
                  <small className="text-muted">
                    Página {currentPage} de {totalPages}
                  </small>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <ListingGridSkeleton color="primary" cardShape="square" />
          ) : error ? (
            <div className="text-center py-5">
              <div className="alert alert-danger d-inline-block">
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
              </div>
              <div className="mt-3">
                <button
                  className="btn btn-primary"
                  onClick={() => window.location.reload()}
                >
                  <i className="fas fa-redo me-2"></i>
                  Reintentar
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="row justify-content-center g-4">
                {currentAgencias.length > 0 ? (
                  currentAgencias.map((item, index) => {
                    const icon = getAgencyIcon(item.nombre);
                    return (
                      <div className="col-xl-3 col-lg-4 col-md-6 col-sm-12" key={`${item.id}-${index}`}>
                        <div className="agents-grid card rounded-3 shadow h-100">
                          <div className="agents-grid-wrap">
                            <div className="fr-grid-thumb mx-auto text-center mt-4 mb-3">
                              <Link to={`/agency-page/${item.id}`} className="d-inline-flex p-1 rounded border">
                                {item.logo ? (
                                  <img 
                                    src={item.logo} 
                                    className="img-fluid rounded" 
                                    width="130" 
                                    height="130" 
                                    alt={item.nombre}
                                    style={{ objectFit: 'cover' }}
                                   loading="lazy" />
                                ) : (
                                  <div 
                                    className={`d-flex align-items-center justify-content-center rounded ${icon.colorClass}`}
                                    style={{ width: '130px', height: '130px' }}
                                  >
                                    <span className="text-white" style={{ fontSize: '48px', fontWeight: 'bold' }}>
                                      {icon.letter}
                                    </span>
                                  </div>
                                )}
                              </Link>
                            </div>
                            <div className="fr-grid-deatil text-center px-3">
                              <h5 className="fr-can-name mb-2 mt-2">
                                <Link 
                                  to={`/agency-page/${item.id}`} 
                                  className="text-decoration-none"
                                  title={item.nombre}
                                >
                                  {item.nombre.length > 25 ? item.nombre.substring(0, 25) + '...' : item.nombre}
                                </Link>
                              </h5>
                              <div className="d-flex align-items-center justify-content-center mb-2">
                                <i className="fa-solid fa-phone text-primary me-2"></i>
                                <span className="agent-property text-muted-2 small">{item.telefono || 'Teléfono no disponible'}</span>
                              </div>
                              {item.correo && (
                                <div className="d-flex align-items-center justify-content-center mb-2">
                                  <i className="fa-solid fa-envelope text-primary me-2"></i>
                                  <span className="text-muted-2 small">{item.correo}</span>
                                </div>
                              )}
                              {item.direccion && (
                                <div className="d-flex align-items-center justify-content-center mb-2">
                                  <i className="fa-solid fa-location-dot text-primary me-2"></i>
                                  <span className="text-muted-2 small">
                                    {(() => {
                                      const formattedAddress = formatAddress(item.direccion);
                                      return formattedAddress.length > 40 ? formattedAddress.substring(0, 40) + '...' : formattedAddress;
                                    })()}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="fr-grid-info d-flex align-items-center justify-content-between px-4 py-4 mt-auto">
                            <Link 
                              to={`/agency-page/${item.id}`} 
                              className="btn btn-light-primary full-width"
                            >
                              Ver Agencia <i className="fa-solid fa-arrow-right-long ms-2"></i>
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-12 text-center py-5">
                    {searchTerm ? (
                      <>
                        <i className="fa-solid fa-search text-muted" style={{ fontSize: '48px' }}></i>
                        <h4 className="mt-3 text-muted">No se encontraron agencias</h4>
                        <p className="text-muted">No hay agencias que coincidan con "{searchTerm}"</p>
                        <button 
                          className="btn btn-outline-primary"
                          onClick={() => setSearchTerm('')}
                        >
                          Limpiar búsqueda
                        </button>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-building text-muted" style={{ fontSize: '48px' }}></i>
                        <h4 className="mt-3 text-muted">No hay agencias registradas</h4>
                        <p className="text-muted">Aún no se han registrado agencias en el sistema</p>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="row mt-5">
                  <div className="col-12">
                    <nav aria-label="Paginación de agencias">
                      <ul className="pagination justify-content-center flex-wrap">
                        {/* Botón Anterior */}
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            <i className="fa-solid fa-chevron-left"></i> Anterior
                          </button>
                        </li>

                        {/* Números de página */}
                        {getPageNumbers().map((pageNumber) => (
                          <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => handlePageChange(pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          </li>
                        ))}

                        {/* Botón Siguiente */}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Siguiente <i className="fa-solid fa-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
